import moment from 'moment';

import get from 'lodash/get';

import { Queue } from './queue';

import { SONG_STATES, getSongStateDescription, Song } from './song';

import { fetchItem } from './storage';

import { getSpotsSegments, getSongInfo, endSong } from './player.service';

import { AudioSingleton } from './AudioSingleton';

import mySound from '../media/silence.mp3';

const today = moment().startOf('day');

let timeoutId = null;
let timeoutWatchDog = null;

let lastSong = null;

// Silence audio to prevent autoplay issues from ../media/silence.mp3
const silence = new Audio(mySound);
silence.loop = true;

export class Player {
	constructor({
		appStore,
		progressBarStore,
		playBarStore,
		initialQueueSize = 1
	}) {
		this.initialQueueSize = initialQueueSize;
		this.appStore = appStore;
		this.playBarStore = playBarStore;
		this.progressBarStore = progressBarStore;
		this.queue = new Queue({
			isOnline: this.appStore.isOnline
		});
		this.spotSegments = null;
		this.paused = true;
		this.currentSong = null;
		this.repaint = this.repaint.bind(this);
	}

	addSpotSegments(segments) {
		this.spotSegments = segments;
	}

	getCurrentStatus() {
		let status = 'UNKNOWN';
		let mediaId = '';
		let volume = '';
		let isSpot = false;
		if (this.currentSong) {
			mediaId = this.currentSong.songId;
			volume = this.currentSong.volume;
			isSpot = this.currentSong.isSpot;
			status = getSongStateDescription(this.currentSong.state);
		} else if (this.queue.isEmpty()) {
			status = 'LOADING';
		} else if (this.playBarStore.isPlaying === false) {
			status = 'STOPPED';
		}
		return {
			mediaId,
			volume,
			isSpot,
			status
		};
	}

	async start() {
		const songs = await this.initQueue();
		if (songs && songs.length > 0) {
			if (songs[0]) {
				this.appStore.currentMusicChannel = songs[0].musicChannel;
			}
			if (!timeoutId) {
				this.repaint();
			}
			if (!timeoutWatchDog) {
				this.watchDogPlay();
			}
			this.appStore.errorStarting = false;
		}
	}

	async play(forcePause = false, autoPlay = false) {
		if (this.appStore.initialStart) {
			silence.play();
		}
		this.appStore.waitingForPlay = true;
		if (this.appStore.isOnline) {
			let jukeboxInfo = null;
			if ((this.currentSong === null || this.currentSong.state === SONG_STATES.ENDED)) {
				const type = get(lastSong, 'type', 'm');

				jukeboxInfo = await endSong(
					get(lastSong, 'songId', ''),
					type
				);
				// JSON.stringify(jukeboxInfo)
			}
			if (jukeboxInfo && jukeboxInfo.response === true) { // Jukebox info received for a new jukebox song

				this.appStore.loadingJukeBox = true;
				const jSong = new Song();
				await jSong.initializeSong(jukeboxInfo);
				this.queue.addJukeBoxSong(jSong);
			}

			const info = await getSongInfo();
			if (info) {
				if (info.musicChannel && info.musicChannel !== this.appStore.currentMusicChannel) {
					// Cambio de canal recibido de appStore.currentMusicChannel a info.musicChannel

					this.appStore.currentMusicChannel = info.musicChannel;
					this.queue.removeAllSongs();
				}

				// '¿Se recibió la solicitud de cambio de lugares? ', info.shouldUpdateSpots

				if (info.shouldUpdateSpots) {
					const segments = await getSpotsSegments();
					// Spots new list received:
					this.queue.removeAllSpots();
					this.addSpotSegments(segments);
				}
				this.queue.forceAdd(info);
			} else if (this.queue.isEmpty()) {
				this.appStore.errorStarting = true;
			}
		}

		if (this.currentSong === null || autoPlay === true) {
			this.currentSong = this.queue.next();
			AudioSingleton.setSong(this.currentSong);
		}

		if (autoPlay && this.appStore.errorStarting === true) {
			this.currentSong = null;
			this.appStore.currentSong = null;
			// Retry to get a new song
			return false;
		}

		// if is still null just skip the instruction
		if (this.currentSong === null) {
			return false;
		}

		if (forcePause || this.currentSong.state === SONG_STATES.PLAYING) {
			this.currentSong.pause();
			this.appStore.wasPlaying = false;
			this.appStore.waitingForPlay = false;
			if (!autoPlay) {
				this.currentSong = null;
				this.appStore.currentSong = null;
				this.playBarStore.currentTime = '00:00 / 00:00';
				this.progressBarStore.progress = 0;
			}
			return false;
		}
		this.currentSong.handleOnPlay = async isUserSignal => {
			if (isUserSignal === false) {
				const isPlaying = await this.play();
				this.playBarStore.isPlaying = isPlaying;
			}
		};
		this.currentSong.play();
		lastSong = this.currentSong;
		this.appStore.waitingForPlay = false;
		if (this.appStore.loadingJukeBox === true) {
			this.appStore.loadingJukeBox = false;
		}
		return true;
	}

	async initQueue() {
		const songs = [];
		try {
			for (let i = 0; i < this.initialQueueSize; i++) {
				let song = await getSongInfo();
				if (!song) {
					throw new Error('No song info available');
				}
				song = await this.queue.forceAdd(song);
				songs.push(song);
			}
			const type = get(lastSong, 'type', 'm')
			const jukeboxInfo = await endSong(
				get(lastSong, 'songId', ''),
				type
			)
			if (jukeboxInfo && jukeboxInfo.response === true) {
				const jSong = new Song()
				await jSong.initializeSong(jukeboxInfo)
				this.queue.addJukeBoxSong(jSong)
				songs.unshift(jSong)
			}
		} catch (err) {
			this.appStore.errorStarting = true;
			return null;
		}
		return songs;
	}

	logout() {
		// Cerrar sesión
		if (timeoutId) {
			clearTimeout(timeoutId);
			timeoutId = null;
		}
		if (timeoutWatchDog) {
			clearTimeout(timeoutWatchDog);
			timeoutWatchDog = null;
		}
		this.appStore.isReady = false;
		this.appStore.errorStarting = false;
		this.appStore.sessionToken = null;
		this.appStore.sessionExpiredMessage = true;
		this.queue.clear();
		if (this.currentSong) {
			this.currentSong.pause();
			this.currentSong = null;
			this.appStore.currentSong = null;
		}
		this.playBarStore.isPlaying = false;
		this.playBarStore.currentTime = '00:00 / 00:00';
		this.progressBarStore.progress = 0;
		this.appStore.wasPlaying = false;
		this.appStore.initialStart = false;
		silence.pause();
	}

	async repaint() {
		const isAlreadyPlaying =
			this.currentSong && this.currentSong.state === SONG_STATES.PLAYING;

		if (!isAlreadyPlaying) {
			this.appStore.isReady = this.queue.isEmpty() === false;
			if (this.appStore.isReady) {
				// autoplay if before was playing
				if (this.appStore.wasPlaying && this.playBarStore.isPlaying === false) {
					this.appStore.wasPlaying = false;
					const isPlaying = await this.play();
					this.playBarStore.isPlaying = isPlaying;
				}
			}
		}

		if (this.appStore.offLineState && this.appStore.isOnline) {

			getSpotsSegments().then(segments => {
				this.addSpotSegments(segments);
				this.initQueue().then(() => {

				});
			});
		}

		this.appStore.offLineState =
			!isAlreadyPlaying && this.queue.isEmpty() && !this.appStore.isOnline;

		const connection =
			navigator.connection ||
			navigator.mozConnection ||
			navigator.webkitConnection;

		if (connection) {
			this.appStore.isSlowConnection = /\slow-2g|2g|3g/.test(
				connection.effectiveType
			);
		}

		const hasToken = fetchItem('APP_TOKEN_SESSION');

		const shouldLogout =
			(!hasToken && !isAlreadyPlaying) ||
			(!hasToken && this.appStore.forceLogout === true);

		if (shouldLogout) {
			this.logout();
			return;
		}

		if (this.spotSegments && isAlreadyPlaying) {
			const rightNow = moment().format('HH:mm');

			const spots = this.spotSegments[rightNow];

			if (spots) {
				console.log('====================================');
				console.log(`Spots for ${rightNow}:`, spots);
				console.log('====================================');
				delete this.spotSegments[rightNow];
				Promise.all(
					spots.map(id => {
						const spot = new Song();
						return spot.initializeSong({
							songId: id,
							isSpot: true
						});
					})
				).then(spots => {
					spots.reverse().forEach(spot => {
						this.queue.forceAddToFirst(spot);
					});
				});
				console.log('====================================');
				console.log('queue', this.queue);
				console.log('====================================');
			}
		}

		if (this.currentSong) {
			if (
				!this.appStore.currentSong ||
				this.appStore.currentSong.songId !== this.currentSong.songId
			) {
				this.appStore.currentSong = this.currentSong;
			}
			if (this.currentSong.state === SONG_STATES.PLAYING) {
				this.progressBarStore.progress = `${this.currentSong.percentageOfTheSong}%`;

				const elapsedTime = today
					.clone()
					.add(this.currentSong.currentSongSecond, 'seconds')
					.format('mm:ss');

				let songTime = get(this.currentSong, 'time', '00:00');

				if (this.currentSong.isSpot) {
					songTime = moment()
						.startOf('day')
						.add(Math.floor(this.currentSong.audio.duration), 'seconds')
						.format('mm:ss');
				}

				this.playBarStore.currentTime = `${elapsedTime} / ${songTime}`;
			} else if (
				this.currentSong.state === SONG_STATES.ENDED ||
				this.currentSong.state === SONG_STATES.ERROR
			) {
				if (this.currentSong.isSpot) {
					this.appStore.currentSong = {
						image: this.currentSong.image,
						isSpot: this.currentSong.isSpot
					};
				}
				this.playBarStore.isPlaying = false;
				this.playBarStore.currentTime = '00:00 / 00:00';
				this.progressBarStore.progress = 0;
				if (fetchItem('APP_TOKEN_SESSION')) {
					this.appStore.wasPlaying = true;
					this.playBarStore.isPlaying = await this.play(false, true);
				}
			} else if (this.currentSong.state === SONG_STATES.PAUSE) {
				if (this.signal !== 'pause') {
					const isPlaying = await this.play(true);
					this.playBarStore.isPlaying = isPlaying;
				}
			}
		}
		timeoutId = setTimeout(async () => {
			await this.repaint();
		}, 100);
	}

	async watchDogPlay() {
		console.log('watchDogPlay online: ', this.appStore.isOnline, 'ready: ', this.appStore.isReady, 'error: ', this.appStore.errorStarting);

		if (this.currentSong && this.currentSong.state === SONG_STATES.PLAYING) {
			return;
		}
		if (this.appStore.isOnline && this.appStore.isReady && this.appStore.errorStarting === true) {
			this.appStore.initialStart = true;
			this.play(true, true);
		} else if (this.appStore.offLineState) {
			this.appStore.wasPlaying = true;
			this.playBarStore.isPlaying = false;
		}
		timeoutWatchDog = setTimeout(() => {
			this.watchDogPlay();
		}, 1000 * 10); // 10 seconds
	}
}
